import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { NotificationSettings, NotificationSettingType, User, UserActivityTimestamp } from '../utils/types'

export const authStore = createSlice({
  name: 'user',
  initialState: {
    username: '',
    token: null as string | null,
    currentUser: {} as User,
    notificationSetting: {} as NotificationSettings,
    activityTimestamps: {} as UserActivityTimestamp
  },
  reducers: {
    setCredentials(state, action) {
      state.username = action.payload.username
      state.token = action.payload.token
    },
    setCurrentUser(state, action: PayloadAction<User>) {
      state.currentUser = action.payload
    },
    setNotificationSettingsState(state, action: PayloadAction<NotificationSettings>) {
      state.notificationSetting = action.payload
    },
    updateNotificationSettings(state, action: PayloadAction<Partial<{[k in NotificationSettingType]: boolean}>>) {
      state.notificationSetting = {...state.notificationSetting, ...action.payload}
    },
    setUserActivityTimestampState(state, action: PayloadAction<UserActivityTimestamp>) {
      state.activityTimestamps = action.payload
    }
  }
})

export type AuthState = ReturnType<typeof authStore.getInitialState>

type AuthRoot = { auth: AuthState }

export const {
  setCredentials,
  setCurrentUser,
  setNotificationSettingsState,
  updateNotificationSettings,
  setUserActivityTimestampState
} = authStore.actions
export const authReducer = authStore.reducer
export const selectUserIsActivated = (state: AuthRoot) => state.auth.currentUser.activated
export const selectToken = (state: AuthRoot) => state.auth.token
export const selectUsername = (state: AuthRoot) => state.auth.currentUser.username
export const selectCurrentUser = (state: AuthRoot) => state.auth.currentUser || {}
export const selectUserFullName = (state: AuthRoot) => state.auth.currentUser.full_name || ''
export const selectNotificationSettings = (state: AuthRoot) => state.auth.notificationSetting || {}
export const selectFirstName = (state: AuthRoot) => selectUserFullName(state).split(' ')[0] || ''
export const selectFirstLetterOfName = (state: AuthRoot) => selectFirstName(state)[0] || ''
export const selectCurrentUserAvatar = (state: AuthRoot) => selectCurrentUser(state).avatar || ''
export const selectCurrentUserLoaded = (state: AuthRoot) => !!Object.keys(state.auth.currentUser).length
export const selectIsLoggedIn = (state: AuthRoot) => !!Object.keys(state.auth.currentUser).length
export const selectActivityTimestamps = (state: AuthRoot) => state.auth.activityTimestamps
export const selectProfileComplete = (state: AuthRoot) => {
  const currentUser = selectCurrentUser(state)
  const fieldsToCheck = ['dob', 'gender', 'avatar', 'instagram', 'linkedin', 'twitter'] as const
  return fieldsToCheck.some(field => !!currentUser[field])
}
